import { apiClient } from './client';
import { unwrapList, unwrapItem } from './unwrap';
import type { PagedResult, Submission, SubmissionProgress } from './types';

/**
 * Matches GET /api/v1/submissions exactly (SubmissionsController.Get):
 * it accepts exactly one of `categoryId`, `competitionId`, or `mine=true` and
 * 400s otherwise ("Provide one of: categoryId, competitionId, mine=true.").
 * There is no server-side pagination and no `status`/`competitorId` filter -
 * callers that need those must filter the returned list client-side.
 */
export interface SubmissionListParams {
  competitionId?: string;
  categoryId?: string;
  mine?: boolean;
}

export const submissionsApi = {
  list: (params: SubmissionListParams = {}) =>
    apiClient
      .get('/submissions', { params })
      .then((r) => unwrapList<Submission>(r.data)) as Promise<PagedResult<Submission>>,

  get: (id: string) => apiClient.get(`/submissions/${id}`).then((r) => unwrapItem<Submission>(r.data)),

  /** GET /submissions/progress?competitionId= - the calling competitor's entry-quota usage per category. */
  getProgress: (competitionId: string) =>
    apiClient.get<SubmissionProgress>('/submissions/progress', { params: { competitionId } }).then((r) => r.data),

  create: (payload: Partial<Submission>) =>
    apiClient.post<Submission>('/submissions', payload).then((r) => r.data),

  update: (id: string, payload: Partial<Submission>) =>
    apiClient.put<Submission>(`/submissions/${id}`, payload).then((r) => r.data),

  /** POST /submissions/{id}/submit - Draft -> Submitted. Competitor-only. */
  submit: (id: string) => apiClient.post<Submission>(`/submissions/${id}/submit`).then((r) => r.data),

  /** POST /submissions/{id}/accept - Submitted -> Accepted. OrganizerStaff-only. */
  accept: (id: string) => apiClient.post<Submission>(`/submissions/${id}/accept`).then((r) => r.data),

  /** POST /submissions/{id}/reject - Submitted -> Rejected. OrganizerStaff-only. */
  reject: (id: string, reason?: string) =>
    apiClient.post<Submission>(`/submissions/${id}/reject`, { reason }).then((r) => r.data),

  /** DELETE /submissions/{id} - owner only, while undecided and submissions are open; removes the submission together with its images. */
  remove: (id: string) => apiClient.delete(`/submissions/${id}`).then(() => undefined),
};
